/* eslint-disable @typescript-eslint/no-explicit-any */
import qs from 'qs';
import { deepEqual, isSSR, omit } from '~/shared/utils';
import { invalidateCache, isAlgoliaCacheValid } from '~/shared/utils/algolia';

/*
 * Algolia default cache system validates cached vs current states.
 * Since facets are dynamic using [*] and page it's 0 in infiniteHits, cache is always invalidated.
 *
 * In this cache the hits are stored with the pathname and url search params.
 * A validation is made based on search params for the especific pathname.
 *
 * Page key is omitted from this comparision and used only to limit the ammount of pages to return form cache,
 * otherwise only hits from one page are shown.
 * */
export const algoliaCache = {
    /*
     * This method is called to read from cache.
     * It returns null if cache is invalidated or no cache is available.
     */
    read() {
        if (!isSSR) {
            try {
                const { search, pathname } = window.location;

                if (!isAlgoliaCacheValid(pathname)) {
                    invalidateCache(pathname);
                    return null;
                }

                const storedHits = sessionStorage.getItem(`algoliaHits-${pathname}`) || '';
                const algoliaHits = JSON.parse(storedHits);

                const currentParams = qs.parse(search.slice(1));
                const sessionPageParams =
                    sessionStorage.getItem(`algoliaParams-${pathname}`) || '?';
                const storedParams = qs.parse(sessionPageParams.slice(1));

                if (areStatesEqual(omit(currentParams, ['page']), omit(storedParams, ['page']))) {
                    return algoliaHits;
                }

                return null;
            } catch (error) {
                return null;
            }
        }
    },
    /*
     * This method is called to write to cache.
     * it stores hits, and search params in a session storage identified by the pathname
     */
    write({ hits }: { hits: any }) {
        if (!isSSR) {
            try {
                const { search, pathname } = window.location;
                try {
                    const currentParams = qs.parse(search.slice(1));
                    const sessionPageParams =
                        sessionStorage.getItem(`algoliaParams-${pathname}`) || '?';
                    const storedParams = qs.parse(sessionPageParams.slice(1));

                    // If stored params page > 1, execept params are the same save ++
                    if (
                        (currentParams.page || 1) > 1 &&
                        deepEqual(omit(currentParams, ['page']), omit(storedParams, ['page']))
                    ) {
                        try {
                            const storedHits =
                                sessionStorage.getItem(`algoliaHits-${pathname}`) || '';
                            const algoliaHits = { ...JSON.parse(storedHits), ...hits };
                            sessionStorage.setItem(
                                `algoliaHits-${pathname}`,
                                JSON.stringify(algoliaHits)
                            );
                            setCacheTimestamp(pathname);
                        } catch (error) {
                            sessionStorage.setItem(`algoliaParams-${pathname}`, search);
                            sessionStorage.setItem(`algoliaHits-${pathname}`, JSON.stringify(hits));
                            setCacheTimestamp(pathname);
                        }
                    } else {
                        // else new cache for page
                        sessionStorage.setItem(`algoliaParams-${pathname}`, search);
                        setCacheTimestamp(pathname);
                        sessionStorage.setItem(
                            `algoliaHits-${pathname}`,
                            JSON.stringify({ 0: hits[0] })
                        );
                    }
                } catch (error) {
                    // Do nothing when an exception occurs
                }
            } catch (exception) {
                // Do nothing when an exception occurs
            }
        }
    },
};

/* *
 *    areStatesEqual - compares the keys in stored state params with existing
 *   ones from current url params
 *
 *    Params
 *
 *   stored - Object
 *   Object with stored keys from cache
 *
 *   current - number
 *   Object with params from current url
 * */
const areStatesEqual = (stored: any, current: any) => {
    const storedKeys = Object.keys(stored);
    for (const key of storedKeys) {
        if (Object.prototype.hasOwnProperty.call(current, key)) {
            let storedKey = stored[key];
            let currentKey = current[key];
            if (Array.isArray(storedKey) && Array.isArray(currentKey)) {
                storedKey = storedKey.filter((x) => x !== '');
                currentKey = currentKey.filter((x) => x !== '');
            }

            /*
             * Since url params can have complex values, after removing empty array entries,
             * compares using deepEqual
             */
            if (!deepEqual(storedKey, currentKey)) {
                return false;
            }
        }
    }

    return true;
};

const setCacheTimestamp = (pathname: string) => {
    if (sessionStorage.getItem(`algoliaUpdateCache-${pathname}`) === null) {
        sessionStorage.setItem(
            `algoliaUpdateCache-${pathname}`,
            Math.floor(Date.now() / 1000).toString()
        );
    }
};
